import { fetchApi } from "@/lib/api";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type UserReport,
  type UserReportDirection,
  type UserReportStatus,
} from "@/components/risk-flagging/user-reports/types";

export interface UserReportsResponse {
  reports: UserReport[];
  total: number;
  page: number;
  limit: number;
}

interface UseUserReportsParams {
  status: UserReportStatus;
  page: number;
  limit: number;
  direction?: UserReportDirection;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const useUserReport = (reportId?: string) => {
  return useQuery<UserReport>({
    queryKey: ["user-report", reportId],
    enabled: !!reportId,
    queryFn: async () => {
      const response = await fetchApi<UserReport>({
        url: `/v2/user-reports/${reportId}`,
        method: "GET",
      });
      return response.data;
    },
  });
};

export const useUserReports = ({
  status,
  page,
  limit,
  direction,
  search,
  dateFrom,
  dateTo,
}: UseUserReportsParams) => {
  return useQuery<UserReportsResponse>({
    queryKey: [
      "user-reports",
      status,
      page,
      limit,
      direction,
      search,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const response = await fetchApi<UserReportsResponse>({
        url: "/v2/user-reports",
        method: "GET",
        params: {
          status,
          page,
          limit,
          ...(direction ? { direction } : {}),
          ...(search?.trim() ? { search: search.trim() } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      });
      return response.data;
    },
  });
};

export const useResolveUserReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      reportId,
      feedbackCorrect,
      note,
    }: {
      reportId: string;
      feedbackCorrect: boolean;
      note?: string;
    }) => {
      const response = await fetchApi<UserReport>({
        url: `/v2/user-reports/${reportId}/resolve`,
        method: "POST",
        data: {
          feedbackCorrect,
          ...(note?.trim() ? { note: note.trim() } : {}),
        },
      });
      return response.data;
    },
    onSuccess: (_data, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: ["user-reports"] });
      queryClient.invalidateQueries({ queryKey: ["user-report", reportId] });
    },
    onError: () => {
      toast({
        title: "Failed to resolve report",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useCreateUserReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      mentorshipId,
      category,
      freeText,
    }: {
      mentorshipId: string;
      category?: string;
      freeText?: string;
    }) => {
      const response = await fetchApi({
        url: "/v2/user-reports",
        method: "POST",
        data: {
          mentorship_id: mentorshipId,
          ...(category ? { category } : {}),
          ...(freeText?.trim() ? { free_text: freeText.trim() } : {}),
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-reports"] });
    },
    onError: () => {
      toast({
        title: "Failed to submit report",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};
